import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMapboxGl, { GeoJSONLayer, ZoomControl, Marker } from 'react-mapbox-gl';
import { styles } from './styles';
import ClustersAndMarkers from './MyCityMapPresenter';
import useMapKeyboardZoom from './hooks/useMapKeyboardZoom';
import { Compass, MapPin } from 'lucide-react';

const USER_LOCATION_ZOOM = 19;
const USER_FOCUS_DELTA = 0.01; // roughly ~1km
const MAX_LOCATE_BOUNDS_DELTA = 0.25;



const MapBox = ReactMapboxGl({
  scrollZoom: true,
  renderChildrenInPortal: true,
  accessToken: process.env.REACT_APP_MAPBOX_TOKEN,
});

const MyCityMapContainer = ({
  zoom,
  goTo,
  center,
  onClick,
  markerHtmlArray,
  isFromCity,
  userLocation,
  selectedPoint,
  subscribeToPoi,
  onClickCluster,
  openInfoWindow,
  showInfoWindow,
  isFromNewReport,
  closeInfoWindow,
  zoomControlClick,
  featureCollection,
  unsubscribeFromPoi,
  fitBounds,
  zoomControl,
  placemarks,
  className,
  resetView,
  defaultFitBounds,
  defaultViewZoom,
  userGpsLocation,
  requestUserLocation,
  isRequestingLocation,
}) => {
  const [mapInstance, setMapInstance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleResize = useCallback(() => {
    if (mapInstance?.resize) {
      mapInstance.resize();
    }
  }, [mapInstance]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const authorityPolygonLayer = useMemo(() => {
    if (!placemarks?.features?.length) {
      return null;
    }

    return (
      <GeoJSONLayer
        data={placemarks}
        fillPaint={{
          'fill-color': 'rgb(136,136,136)',
          'fill-opacity': 0.3,
          'fill-outline-color': 'rgb(120,120,120)',
        }}
      />
    );
  }, [placemarks]);

  const handleStyleLoad = useCallback((map) => {
    setMapInstance((current) => current || map);
    setIsLoading((prev) => (prev ? false : prev));
  }, []);

  const handleLocateMe = useCallback(() => {
    requestUserLocation?.();

    if (!mapInstance) {
      return;
    }

    const hasGpsLocation =
      Number.isFinite(userGpsLocation?.lng) &&
      Number.isFinite(userGpsLocation?.lat);
    if (!hasGpsLocation) {
      return;
    }

    const baseBounds =
      (Array.isArray(fitBounds) && fitBounds.length === 4 && fitBounds) ||
      (Array.isArray(defaultFitBounds) &&
        defaultFitBounds.length === 4 &&
        defaultFitBounds) ||
      null;

    const fallbackBounds = [
      userGpsLocation.lng - USER_FOCUS_DELTA,
      userGpsLocation.lat - USER_FOCUS_DELTA,
      userGpsLocation.lng + USER_FOCUS_DELTA,
      userGpsLocation.lat + USER_FOCUS_DELTA,
    ];

    const targetBounds = (() => {
      if (!baseBounds) {
        return fallbackBounds;
      }
      const expandedBounds = [
        Math.min(baseBounds[0], userGpsLocation.lng),
        Math.min(baseBounds[1], userGpsLocation.lat),
        Math.max(baseBounds[2], userGpsLocation.lng),
        Math.max(baseBounds[3], userGpsLocation.lat),
      ];

      const deltaLng = Math.abs(expandedBounds[2] - expandedBounds[0]);
      const deltaLat = Math.abs(expandedBounds[3] - expandedBounds[1]);

      if (
        deltaLng > MAX_LOCATE_BOUNDS_DELTA ||
        deltaLat > MAX_LOCATE_BOUNDS_DELTA
      ) {
        return fallbackBounds;
      }

      return expandedBounds;
    })();

    mapInstance.fitBounds(targetBounds, {
      padding: 50,
      duration: 500,
    });
  }, [
    requestUserLocation,
    mapInstance,
    userGpsLocation,
    fitBounds,
    defaultFitBounds,
  ]);

  const hasAuthorityCenter =
    Array.isArray(center) &&
    center.length === 2 &&
    center.every((value) => Number.isFinite(value));
  const hasProfileLocation =
    Number.isFinite(userLocation?.lat) && Number.isFinite(userLocation?.lng);
  const canResetView = Boolean(resetView) && (hasProfileLocation || hasAuthorityCenter);
  const canUseGeolocation = Boolean(requestUserLocation);

  const handleResetView = useCallback(() => {
    if (!mapInstance) {
      return;
    }

    const hasUserLocation =
      Number.isFinite(userLocation?.lat) && Number.isFinite(userLocation?.lng);
    const hasCenter =
      Array.isArray(center) &&
      center.length === 2 &&
      center.every((value) => Number.isFinite(value));

    const activeBounds = Array.isArray(fitBounds) && fitBounds.length === 4
      ? fitBounds
      : Array.isArray(defaultFitBounds) && defaultFitBounds.length === 4
        ? defaultFitBounds
        : null;

    if (activeBounds) {
      mapInstance.fitBounds(activeBounds, {
        padding: 50,
        duration: 500,
      });
      resetView?.();
      return;
    }

    const targetCenter = hasCenter
      ? center
      : hasUserLocation
        ? [userLocation.lng, userLocation.lat]
        : null;

    if (!targetCenter) {
      return;
    }

    const fallbackZoom = Number.isFinite(defaultViewZoom)
      ? defaultViewZoom
      : Array.isArray(zoom) && Number.isFinite(zoom[0])
        ? zoom[0]
        : USER_LOCATION_ZOOM;

    mapInstance.flyTo({
      center: targetCenter,
      zoom: fallbackZoom,
      duration: 500,
    });

    resetView?.();
  }, [
    mapInstance,
    fitBounds,
    defaultFitBounds,
    defaultViewZoom,
    userLocation,
    center,
    zoom,
    resetView,
  ]);

  const mapContainerStyle = useMemo(
    () => ({ minHeight: isFromCity ? 450 : 190, flexGrow: 1 }),
    [isFromCity]
  );

  useMapKeyboardZoom(mapInstance, showInfoWindow, zoomControlClick, {
    onLocate: canUseGeolocation ? handleLocateMe : undefined,
    onReset: canResetView ? handleResetView : undefined,
  });

  return (
    <div className="relative" style={mapContainerStyle}>
      <MapBox
        zoom={zoom}
        center={center}
        className={className}
        style={styles.basic}
        fitBounds={fitBounds}
        containerStyle={{ minHeight: isFromCity ? 450 : 190, flexGrow: 1 }}
        onClick={(map, e) => (isFromCity ? e.preventDefault() : onClick())}
        onStyleLoad={handleStyleLoad}
      >
        {!isLoading && (
          <>
            <ClustersAndMarkers
              zoom={zoom}
              goTo={goTo}
              markerHtmlArray={markerHtmlArray}
              isFromCity={isFromCity}
              userLocation={userLocation}
              selectedPoint={selectedPoint}
              subscribeToPoi={subscribeToPoi}
              onClickCluster={onClickCluster}
              openInfoWindow={openInfoWindow}
              showInfoWindow={showInfoWindow}
              isFromNewReport={isFromNewReport}
              closeInfoWindow={closeInfoWindow}
              featureCollection={featureCollection}
              unsubscribeFromPoi={unsubscribeFromPoi}
            />
            {authorityPolygonLayer}
          </>
        )}
        {!zoomControl && (
          <ZoomControl
            onControlClick={(map, zoomDiff) => {
              if (zoomDiff > 0)
                map.zoomIn({ level: map.getZoom() + zoomDiff });
              else {
                map.zoomOut({ level: map.getZoom() - zoomDiff });
                if (showInfoWindow) {
                  zoomControlClick();
                }
              }
            }}
          />
        )}
        {userGpsLocation &&
          Number.isFinite(userGpsLocation?.lng) &&
          Number.isFinite(userGpsLocation?.lat) && (
            <Marker
              coordinates={[userGpsLocation.lng, userGpsLocation.lat]}
              anchor="bottom"
            >
              <span className="relative flex h-10 w-10 items-center justify-center animate-pulse shadow-lg shadow-blue-500/20">
                <span className="absolute inline-flex h-full w-full rounded-full bg-blue-500/30 animate-pulse" />
                <span className="absolute inline-flex h-full w-full rounded-full bg-blue-500/20 blur-sm border border-white/40" />
                <span className="relative inline-flex h-4.5 w-4.5 items-center justify-center rounded-full border-2 border-teal-500 bg-blue-700 shadow-xl">
                  <span className="h-2 w-2 rounded-full bg-teal-500" />
                </span>
              </span>
            </Marker>
          )}
      </MapBox>
      {!isLoading && (canResetView || canUseGeolocation) && (
        <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-2">
          {canResetView && (
            <button
              type="button"
              onClick={handleResetView}
              className="rounded bg-white p-2 shadow focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:bg-gray-50 disabled:opacity-50"
              title="Reset view"
              aria-label="Reset view"
              disabled={!canResetView}
            >
              <Compass className="h-5 w-5 text-gray-800" />
            </button>
          )}
          {canUseGeolocation && (
            <button
              type="button"
              onClick={handleLocateMe}
              className="rounded bg-white p-2 shadow focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:bg-gray-50 disabled:opacity-50"
              title="Go to my location"
              aria-label="Go to my location"
              disabled={isRequestingLocation}
            >
              {isRequestingLocation ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
              ) : (
                <MapPin className="h-5 w-5 text-gray-800" />
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MyCityMapContainer;
